import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [needsSignup, setNeedsSignup] = useState(false);
  const [paysOwnSeat, setPaysOwnSeat] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setError("Missing invite token."); setLoading(false); return; }
    api.get(`/teams/invite/${token}`)
      .then(({ data }) => setInvite(data))
      .catch(e => setError(e?.response?.data?.detail || "Invite invalid."))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    setSubmitting(true);
    try {
      const payload = { token, pays_own_seat: paysOwnSeat, origin_url: window.location.origin };
      if (needsSignup) {
        if (!name.trim() || password.length < 6) {
          toast.error("Enter your name and a password of at least 6 characters.");
          setSubmitting(false);
          return;
        }
        payload.name = name.trim();
        payload.password = password;
      }
      const { data } = await api.post("/teams/accept", payload);
      if (setAuth) setAuth(data.token, data.user);
      toast.success(`Welcome to ${data.team.name}!`);
      // If they chose self-pay, redirect to Stripe checkout. Otherwise to dashboard.
      if (data.self_checkout_url) {
        window.location.href = data.self_checkout_url;
        return;
      }
      navigate("/dashboard");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not accept invite.";
      if (detail.includes("doesn't exist")) {
        setNeedsSignup(true);
        toast.info("Please create a password to finish joining.");
      } else {
        toast.error(detail);
      }
    } finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ maxWidth: 500, margin: "80px auto", padding: 24 }}><div className="shimmer" style={{ height: 200, borderRadius: 14 }} /></div>;
  if (error) return (
    <div style={{ maxWidth: 500, margin: "80px auto", padding: 30, textAlign: "center" }} className="bg-card">
      <div style={{ fontSize: 40, color: "#FF6347", marginBottom: 10 }}>✖</div>
      <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 8 }}>Invite issue</div>
      <div className="muted">{error}</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 500, margin: "60px auto", padding: 24 }}>
      <div className="bg-card" style={{ padding: 32 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 12 }}>YOU'VE BEEN INVITED</div>
        <h1 className="serif" style={{ fontSize: 30, color: "#fff", margin: 0, fontWeight: 400 }}>{invite.team_name}</h1>
        <p className="muted" style={{ marginTop: 14, lineHeight: 1.7 }}>
          <b style={{ color: "#ddd" }}>{invite.invited_by_email}</b> has invited <b style={{ color: "#ddd" }}>{invite.email}</b>
          {" "}to collaborate on memorial work.
        </p>
        <div style={{ padding: 14, background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.4)", borderRadius: 10, marginTop: 16, color: "#D4AF37" }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>💎 Joining is FREE for you</div>
          <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.5 }}>Your team owner pays for your seat ($7/mo on their bill). You will NOT be charged.</div>
        </div>

        {needsSignup && (
          <div style={{ marginTop: 20 }}>
            <label className="muted" style={{ fontSize: 12, letterSpacing: "0.1em" }}>YOUR NAME</label>
            <input data-testid="accept-name" value={name} onChange={e => setName(e.target.value)} placeholder="Full name" style={{ width: "100%", padding: 12, marginTop: 6, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8 }} />
            <label className="muted" style={{ fontSize: 12, letterSpacing: "0.1em", marginTop: 14, display: "block" }}>PASSWORD</label>
            <input data-testid="accept-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" style={{ width: "100%", padding: 12, marginTop: 6, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8 }} />
          </div>
        )}

        {/* Seat-payer choice */}
        <div style={{ marginTop: 22, padding: 16, background: "#0a0a0b", border: "1px solid #1f1f1f", borderRadius: 10 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.25em", color: "var(--gold)", marginBottom: 12 }}>WHO PAYS FOR YOUR SEAT?</div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 8px", cursor: "pointer", borderRadius: 6, background: !paysOwnSeat ? "rgba(212,175,55,0.08)" : "transparent" }}>
            <input data-testid="seat-owner-pays" type="radio" checked={!paysOwnSeat} onChange={() => setPaysOwnSeat(false)} style={{ marginTop: 4, accentColor: "var(--gold)" }} />
            <div>
              <div style={{ color: "#fff", fontSize: 14 }}>My team owner pays</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>FREE for me. $7/mo added to {invite.invited_by_email}'s bill.</div>
            </div>
          </label>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 8px", cursor: "pointer", borderRadius: 6, marginTop: 4, background: paysOwnSeat ? "rgba(212,175,55,0.08)" : "transparent" }}>
            <input data-testid="seat-self-pays" type="radio" checked={paysOwnSeat} onChange={() => setPaysOwnSeat(true)} style={{ marginTop: 4, accentColor: "var(--gold)" }} />
            <div>
              <div style={{ color: "#fff", fontSize: 14 }}>I'll pay my own seat ($7/mo)</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>I'll be redirected to Stripe to add a card after accepting. The team owner pays nothing extra.</div>
            </div>
          </label>
        </div>

        <button data-testid="accept-btn" onClick={accept} disabled={submitting} className="btn-gold" style={{ width: "100%", marginTop: 22 }}>
          {submitting ? "Joining…" : (needsSignup ? "Create account & join" : "Accept invitation")}
        </button>
        <div className="muted" style={{ fontSize: 11, marginTop: 12, textAlign: "center" }}>
          Free for you. Your team owner covers your seat.
        </div>
      </div>
    </div>
  );
}
